import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Star, UserPlus } from "lucide-react";
import { type DefaultValues, useForm } from "react-hook-form";
import { z } from "zod";
import { FormDialog, useCloseOrError } from "@/shared/components/FormDialog";
import { RaidRoleSelectItems } from "@/shared/components/RaidRoleSelectItems";
import { Button } from "@/shared/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { WowClassSelectItems } from "@/shared/components/WowClassSelectItems";
import type { CharacterResponse } from "@/shared/lib/charactersApi";
import {
  useCreateCharacter,
  useDeleteCharacter,
  useSetMainCharacter,
  useUpdateCharacter,
} from "@/shared/lib/charactersApi";
import { RAID_ROLES, type RaidRole } from "@/shared/lib/roles";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import { WOW_CLASSES, type WowClass } from "@/shared/lib/wowClass";

export type PlayerOption = { id: string; name: string };

// requireUser adds a player-select requirement (create mode without a preset user).
function buildSchema(requireUser: boolean) {
  const base = z.object({
    userId: z.string(),
    name: z.string().trim().min(1, "Namn krävs.").max(32, "Högst 32 tecken."),
    wowClass: z.enum(WOW_CLASSES, { message: "Välj klass." }),
    raidRole: z.enum(RAID_ROLES).nullable(),
  });
  if (!requireUser) return base;
  return base.refine((v) => v.userId.length > 0, {
    path: ["userId"],
    message: "Välj spelare.",
  });
}

const characterSchema = buildSchema(false);
export type CharacterFormInput = z.input<typeof characterSchema>;
export type CharacterFormOutput = z.output<typeof characterSchema>;

function seedValues(
  presetUserId: string,
  character?: CharacterResponse,
): DefaultValues<CharacterFormInput> {
  return {
    userId: character?.userId ?? presetUserId,
    name: character?.name ?? "",
    wowClass: (character?.wowClass ?? undefined) as WowClass | undefined,
    raidRole: (character?.raidRole ?? null) as RaidRole | null,
  };
}

export function CharacterDialog({
  open,
  onOpenChange,
  userId,
  players,
  character,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  // Preset player for create mode. Omit to let the user pick from `players`.
  userId?: string;
  // Player options for the select, used only when creating without a preset user.
  players?: PlayerOption[];
  // Present → edit; absent → create.
  character?: CharacterResponse;
}) {
  const mode = character ? "edit" : "create";
  const requireUserSelect = mode === "create" && !userId;

  const form = useForm<CharacterFormInput, unknown, CharacterFormOutput>({
    resolver: zodResolver(buildSchema(requireUserSelect)),
    defaultValues: seedValues(userId ?? "", character),
  });

  useResetOnOpen(open, () => form.reset(seedValues(userId ?? "", character)));

  const create = useCreateCharacter();
  const update = useUpdateCharacter();
  const setMain = useSetMainCharacter();
  const del = useDeleteCharacter();
  const closeOrError = useCloseOrError(onOpenChange);

  const onSubmit = (values: CharacterFormOutput) => {
    if (mode === "create") {
      create.mutate(
        {
          userId: values.userId,
          name: values.name,
          wowClass: values.wowClass,
          raidRole: values.raidRole,
        },
        closeOrError("Kunde inte skapa karaktär"),
      );
    } else if (character) {
      update.mutate(
        {
          id: character.id,
          body: {
            name: values.name,
            wowClass: values.wowClass,
            raidRole: values.raidRole,
          },
        },
        closeOrError("Kunde inte uppdatera karaktär"),
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "create" ? "Ny karaktär" : "Redigera karaktär"}
      form={form}
      onSubmit={onSubmit}
      submitting={
        create.isPending ||
        update.isPending ||
        setMain.isPending ||
        del.isPending
      }
      submitLabel={mode === "create" ? "Lägg till" : "Spara"}
      submitIcon={mode === "create" ? <UserPlus /> : <Save />}
      onDelete={
        character
          ? () =>
              del.mutate(
                character.id,
                closeOrError("Kunde inte ta bort karaktär"),
              )
          : undefined
      }
      deleting={del.isPending}
    >
      {requireUserSelect && (
        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Spelare</FormLabel>
              <Select
                value={field.value || undefined}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Välj spelare" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(players ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Namn</FormLabel>
            <FormControl>
              <Input maxLength={32} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="raidRole"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Roll</FormLabel>
            <Select
              value={field.value ?? undefined}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Välj roll" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <RaidRoleSelectItems />
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="wowClass"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Klass</FormLabel>
            <Select
              value={field.value ?? undefined}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Välj klass" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <WowClassSelectItems />
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {character && !character.isMain && (
        <Button
          type="button"
          variant="outline"
          loading={setMain.isPending}
          onClick={() =>
            setMain.mutate(character.id, closeOrError("Kunde inte sätta main"))
          }
        >
          <Star />
          Gör till main
        </Button>
      )}
    </FormDialog>
  );
}
